// shared/utils/storage.js
// SessionStorage and localStorage utilities

/**
 * Save journey ID to session storage
 * @param {string} journeyId 
 */
export const saveSelectedJourney = (journeyId) => {
  sessionStorage.setItem('selectedJourneyId', journeyId);
};

/**
 * Get selected journey ID
 * @returns {string|null}
 */
export const getSelectedJourney = () => {
  return sessionStorage.getItem('selectedJourneyId');
};

/**
 * Clear selected journey
 */
export const clearSelectedJourney = () => {
  sessionStorage.removeItem('selectedJourneyId');
};

/**
 * Save session resume data
 * @param {string} sessionId 
 * @param {string} journeyId 
 * @param {string} step 
 */
export const saveResumeData = (sessionId, journeyId, step) => {
  sessionStorage.setItem('resumeSessionId', sessionId);
  sessionStorage.setItem('selectedJourneyId', journeyId);
  sessionStorage.setItem('resumeStep', step);
};

/**
 * Get resume data
 * @returns {Object} {sessionId, journeyId, step}
 */
export const getResumeData = () => {
  return {
    sessionId: sessionStorage.getItem('resumeSessionId'),
    journeyId: sessionStorage.getItem('selectedJourneyId'),
    step: sessionStorage.getItem('resumeStep')
  };
};

/**
 * Clear resume data
 */
export const clearResumeData = () => {
  sessionStorage.removeItem('resumeSessionId');
  sessionStorage.removeItem('selectedJourneyId');
  sessionStorage.removeItem('resumeStep');
};

/**
 * Clear all session data
 */
export const clearAllSessionData = () => {
  clearSelectedJourney();
  clearResumeData();
};

/**
 * Check if there's resume data available
 * @returns {boolean}
 */
export const hasResumeData = () => {
  const { sessionId, journeyId } = getResumeData();
  return !!(sessionId && journeyId && journeyId !== 'undefined' && journeyId !== 'null');
};

/**
 * Save to local storage with expiry
 * @param {string} key 
 * @param {any} value 
 * @param {number} expiryDays - Days until expiry
 */
export const saveWithExpiry = (key, value, expiryDays = 7) => {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + (expiryDays * 24 * 60 * 60 * 1000)
  };
  localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Get from local storage with expiry check
 * @param {string} key 
 * @returns {any|null}
 */
export const getWithExpiry = (key) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  
  try {
    const item = JSON.parse(itemStr);
    const now = new Date();
    
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    
    return item.value;
  } catch {
    return null;
  }
};

/**
 * Save object to session storage
 * @param {string} key 
 * @param {Object} obj 
 */
export const saveObject = (key, obj) => {
  sessionStorage.setItem(key, JSON.stringify(obj));
};

/**
 * Get object from session storage
 * @param {string} key 
 * @returns {Object|null}
 */
export const getObject = (key) => {
  const item = sessionStorage.getItem(key);
  if (!item) return null;
  
  try {
    return JSON.parse(item);
  } catch {
    return null;
  }
};

/**
 * Check if storage is available
 * @param {string} type - 'localStorage' or 'sessionStorage'
 * @returns {boolean}
 */
export const isStorageAvailable = (type = 'sessionStorage') => {
  try {
    const storage = window[type];
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};
